import type { FinancingProduct } from '../../store/oneButtonStore';
import {
    columnIncludesBattery,
    columnIncludesRoof,
    columnIncludesSolar,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/quoteCreationProducts';
import type { PreContractProduct } from './preContractTypes';

/** Financing grid columns eligible for pre-contract combination selection. */
export const PCI_PRODUCT_COMBINATION_COLUMNS: FinancingProduct[] = [
    'Solar',
    'Roof',
    'Solar + Roof',
    'Battery',
    'Battery + Roof',
    'Solar + Battery',
];

const PCI_PRODUCT_TO_HUB: Record<PreContractProduct, string> = {
    Solar: 'Solar',
    Roof: 'Roof',
    Battery: 'Battery',
    RR: 'RR',
    RRR: 'RRR',
};

export function preContractHubProductName(product: PreContractProduct): string {
    return PCI_PRODUCT_TO_HUB[product];
}

export function buildPreContractProductCombinations(
    selectedProducts: PreContractProduct[],
): FinancingProduct[] {
    const hasSolar = selectedProducts.includes('Solar');
    const hasRoof = selectedProducts.includes('Roof');
    const hasBattery = selectedProducts.includes('Battery');

    return PCI_PRODUCT_COMBINATION_COLUMNS.filter((column) => {
        if (columnIncludesSolar(column) && !hasSolar) return false;
        if (columnIncludesRoof(column) && !hasRoof) return false;
        if (columnIncludesBattery(column) && !hasBattery) return false;
        return true;
    });
}

/** Hub product lines for every product selected on the intake board. */
export function selectedHubProductNames(selectedProducts: PreContractProduct[]): string[] {
    return selectedProducts.map((product) => PCI_PRODUCT_TO_HUB[product]);
}

/** Hub product lines included in the active financing-grid column. */
export function hubProductsInCombination(
    column: FinancingProduct,
    selectedProducts: PreContractProduct[],
): string[] {
    const names: string[] = [];
    if (columnIncludesSolar(column) && selectedProducts.includes('Solar')) {
        names.push(PCI_PRODUCT_TO_HUB.Solar);
    }
    if (columnIncludesRoof(column) && selectedProducts.includes('Roof')) {
        names.push(PCI_PRODUCT_TO_HUB.Roof);
    }
    if (columnIncludesBattery(column) && selectedProducts.includes('Battery')) {
        names.push(PCI_PRODUCT_TO_HUB.Battery);
    }
    return names;
}

export function financeCellKey(partner: string, column: FinancingProduct): string {
    return `${partner}::${column}`;
}

export function parseFinanceCellKey(key: string): { partner: string; column: string } | null {
    const idx = key.indexOf('::');
    if (idx < 0) return null;
    return { partner: key.slice(0, idx), column: key.slice(idx + 2) };
}

export function selectedCombinationColumn(
    selectedFinanceCells: Set<string>,
    availableColumns: FinancingProduct[],
): FinancingProduct | null {
    for (const column of availableColumns) {
        for (const key of selectedFinanceCells) {
            const parsed = parseFinanceCellKey(key);
            if (parsed?.column === column) return column;
        }
    }
    return null;
}

export function selectedPartnerForColumn(
    selectedFinanceCells: Set<string>,
    column: FinancingProduct,
): string | null {
    for (const key of selectedFinanceCells) {
        const parsed = parseFinanceCellKey(key);
        if (parsed?.column === column) return parsed.partner;
    }
    return null;
}

export function combinationIncludesSolar(column: FinancingProduct): boolean {
    return columnIncludesSolar(column);
}

/** Product line that drives the financing-grid partner for a combo column. */
export function primaryPartnerSourceProduct(
    column: FinancingProduct,
    hubProducts: string[],
): string | null {
    if (combinationIncludesSolar(column) && hubProducts.includes('Solar')) return 'Solar';
    if (columnIncludesRoof(column) && hubProducts.includes('Roof')) return 'Roof';
    if (columnIncludesBattery(column) && hubProducts.includes('Battery')) return 'Battery';
    return hubProducts[0] ?? null;
}

export type CombinationPartnerOption = {
    value: string;
    label: string;
    disabled?: boolean;
    disabledReason?: string;
};

/** Partners available for every product line in a combination (Products popup rules). */
export function intersectPartnerOptionsForProducts(
    productPartnerLists: CombinationPartnerOption[][],
): CombinationPartnerOption[] {
    if (productPartnerLists.length === 0) return [];
    const [first, ...rest] = productPartnerLists;
    const eligibleFirst = first.filter((option) => option.value);
    return eligibleFirst.filter((option) =>
        rest.every((list) => list.some((entry) => entry.value === option.value)),
    );
}
